import React from 'react';
import Layout from '../components/layout/Layout';
import { Link } from "react-router-dom";
import CTASection from "../components/common/CTASection";
import hero from "../assets/clienthero.jpg";
import { Building2, Utensils, School, Heart, Landmark, Users } from 'lucide-react';

const ClientsPage = () => {
  const industrialClients = [
    "Suzlon Energy Ltd. (Khopadva)",
    "GHCL Limited (Bhilad)",
    "Waaree Energies Ltd. Tumb",
    "Waaree Energies Ltd. Nandigram",
    "Waaree Energies Ltd. Unn Navsari",
    "Waaree Energies Ltd. Shyamkhyali",
    "Captech Private Ltd. (Umargaon)",
    "CMP Machinery & Plastic (Umargaon)",
    "Adani (Mundra Port)",
    "Adani (Khavda-Kutch)"
  ];

  const restaurantClients = [
    "Krishna Restaurant (Valsad)",
    "Holiday Inn — Guest House (Mehsana)",
    "Vaibhav Hotel & Guest House (Ahmedabad)",
    "Rajwadi Hotel (Udwada)",
    "Ashapura Punjabi Restaurant (Bhuj)",
    "Ashapura Jain Gujarati Thali Restaurant (Bhuj)",
    "Payal Restaurant (Bhuj)",
    "Rangoli Hotel (Mundra Port)"
  ];

  const schoolClients = [
    "Shree Swaminarayan School (Bhuj)",
    "White House Public School (Bhuj)",
    "Army School (Bhuj)",
    "Chanakya School (Bhuj)"
  ];

  const hospitalClients = [
    "Shree Swaminarayan (MMPJ / C.L. Patel) Hospital (Bhuj)",
    "G.K. General Hospital (Bhuj)",
    "Shalby Hospital (Ahmedabad)",
    "Zydus Hospital (Ahmedabad)",
    "Geetanjali Hospital (Udaipur)"
  ];

  const templeClients = [
    "Yaksh Mandir (Jain Institution)",
    "Shree Swaminarayan Temple (Bhuj)"
  ];

  const institutionalClients = [
    "Navneet (Jain Community Group)",
    "Seth Dosabhai Lalchand (Jain Community Group)",
    "VBC Nutan Seva Sankul (Jain Community Group)"
  ];

  return (
    <Layout>
      {/* ===== HERO SECTION ===== */}
      <section 
        className="relative py-16 sm:py-20 bg-fixed bg-cover bg-center"
        style={{ 
          backgroundImage: `url(${hero})`,
          backgroundAttachment: "fixed"
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[#2F3C7E]/70"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
            Our Clients
          </h1>
          <p className="text-lg sm:text-xl text-[#E6B325]">
            Trusted by Industry Leaders
          </p>
        </div>
      </section>

      {/* ===== INDUSTRIAL CLIENTS SECTION ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {/* Section Header with Icon */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="p-3 bg-[#2F3C7E] rounded-lg">
              <Building2 className="w-8 h-8 text-[#E6B325]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2F3C7E]">
              INDUSTRIAL
            </h2>
          </div>

          {/* Clients Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {industrialClients.map((client, index) => {
              const hasLocation = client.includes('(');
              const name = hasLocation ? client.substring(0, client.indexOf('(')).trim() : client;
              const location = hasLocation ? client.substring(client.indexOf('(')) : '';

              return (
                <div 
                  key={index} 
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition transform hover:-translate-y-1 border border-gray-100 text-center"
                >
                  <div className="flex justify-center mb-4">
                    <div className="bg-[#E6B325]/10 p-3 rounded-full">
                      <Building2 className="w-6 h-6 text-[#2F3C7E]" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-2">
                    {name}
                  </h3>
                  {location && (
                    <p className="text-gray-500 text-sm">
                      {location}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== RESTAURANTS / HOTELS / GUEST HOUSES SECTION ===== */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* Section Header with Icon */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="p-3 bg-[#2F3C7E] rounded-lg">
              <Utensils className="w-8 h-8 text-[#E6B325]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2F3C7E]">
              RESTAURANTS / HOTELS / GUEST HOUSES
            </h2>
          </div>

          {/* Clients Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {restaurantClients.map((client, index) => {
              const hasLocation = client.includes('(');
              const name = hasLocation ? client.substring(0, client.indexOf('(')).trim() : client;
              const location = hasLocation ? client.substring(client.indexOf('(')) : '';

              return (
                <div 
                  key={index} 
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition transform hover:-translate-y-1 border border-gray-100 text-center"
                >
                  <div className="flex justify-center mb-4">
                    <div className="bg-[#E6B325]/10 p-3 rounded-full">
                      <Utensils className="w-6 h-6 text-[#2F3C7E]" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-2">
                    {name}
                  </h3>
                  {location && (
                    <p className="text-gray-500 text-sm">
                      {location}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SCHOOLS SECTION ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {/* Section Header with Icon */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="p-3 bg-[#2F3C7E] rounded-lg">
              <School className="w-8 h-8 text-[#E6B325]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2F3C7E]">
              SCHOOLS
            </h2>
          </div>

          {/* Clients Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {schoolClients.map((client, index) => {
              const hasLocation = client.includes('(');
              const name = hasLocation ? client.substring(0, client.indexOf('(')).trim() : client;
              const location = hasLocation ? client.substring(client.indexOf('(')) : '';

              return (
                <div 
                  key={index} 
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition transform hover:-translate-y-1 border border-gray-100 text-center"
                >
                  <div className="flex justify-center mb-4">
                    <div className="bg-[#E6B325]/10 p-3 rounded-full">
                      <School className="w-6 h-6 text-[#2F3C7E]" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-2">
                    {name}
                  </h3>
                  {location && (
                    <p className="text-gray-500 text-sm">
                      {location}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== HOSPITALS SECTION ===== */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* Section Header with Icon */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="p-3 bg-[#2F3C7E] rounded-lg">
              <Heart className="w-8 h-8 text-[#E6B325]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2F3C7E]">
              HOSPITALS
            </h2>
          </div>

          {/* Clients Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {hospitalClients.map((client, index) => {
              const hasLocation = client.includes('(');
              const name = hasLocation ? client.substring(0, client.indexOf('(')).trim() : client;
              const location = hasLocation ? client.substring(client.indexOf('(')) : '';

              return (
                <div 
                  key={index} 
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition transform hover:-translate-y-1 border border-gray-100 text-center"
                >
                  <div className="flex justify-center mb-4">
                    <div className="bg-[#E6B325]/10 p-3 rounded-full">
                      <Heart className="w-6 h-6 text-[#2F3C7E]" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-lg mb-2">
                    {name}
                  </h3>
                  {location && (
                    <p className="text-gray-500 text-sm">
                      {location}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== TEMPLES / DHARMIK SECTION ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          {/* Section Header with Icon */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="p-3 bg-[#2F3C7E] rounded-lg">
              <Landmark className="w-8 h-8 text-[#E6B325]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2F3C7E]">
              TEMPLES / DHARMIK
            </h2>
          </div>

          {/* Clients Cards Grid - Centered for 2 cards */}
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {templeClients.map((client, index) => {
                const hasLocation = client.includes('(');
                const name = hasLocation ? client.substring(0, client.indexOf('(')).trim() : client;
                const location = hasLocation ? client.substring(client.indexOf('(')) : '';

                return (
                  <div 
                    key={index} 
                    className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition transform hover:-translate-y-1 border border-gray-100 text-center"
                  >
                    <div className="flex justify-center mb-4">
                      <div className="bg-[#E6B325]/10 p-3 rounded-full">
                        <Landmark className="w-6 h-6 text-[#2F3C7E]" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-lg mb-2">
                      {name}
                    </h3>
                    {location && (
                      <p className="text-gray-500 text-sm">
                        {location}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== INSTITUTIONAL SECTION ===== */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* Section Header with Icon */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <div className="p-3 bg-[#2F3C7E] rounded-lg">
              <Users className="w-8 h-8 text-[#E6B325]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2F3C7E]">
              INSTITUTIONAL
            </h2>
          </div>

          {/* Clients Cards Grid - Centered for 3 cards */}
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {institutionalClients.map((client, index) => {
                const hasLocation = client.includes('(');
                const name = hasLocation ? client.substring(0, client.indexOf('(')).trim() : client;
                const location = hasLocation ? client.substring(client.indexOf('(')) : '';

                return (
                  <div 
                    key={index} 
                    className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition transform hover:-translate-y-1 border border-gray-100 text-center"
                  >
                    <div className="flex justify-center mb-4">
                      <div className="bg-[#E6B325]/10 p-3 rounded-full">
                        <Users className="w-6 h-6 text-[#2F3C7E]" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-lg mb-2">
                      {name}
                    </h3>
                    {location && (
                      <p className="text-gray-500 text-sm">
                        {location}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <CTASection />
    </Layout>
  );
};

export default ClientsPage;